import { useState, type FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showResetInfo, setShowResetInfo] = useState(false);

  // 이미 로그인된 상태면 홈으로
  if (isAuthenticated) {
    navigate('/', { replace: true });
    return null;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    const result = await login(email, password, rememberMe);

    setIsSubmitting(false);

    if (result.success) {
      navigate('/', { replace: true });
    } else {
      setError(result.error || '로그인에 실패했습니다');
    }
  };

  return (
    <div className="min-h-screen max-w-lg mx-auto bg-background-50 flex flex-col">
      {/* 상단 따뜻한 그라데이션 영역 */}
      <div className="relative w-full pt-16 pb-10 px-6 flex flex-col items-center text-center bg-gradient-to-b from-accent-100/60 via-secondary-100/40 to-background-50">
        {/* 장식용 원형 요소 */}
        <div className="absolute top-8 right-10 w-20 h-20 rounded-full bg-accent-200/30"></div>
        <div className="absolute top-20 left-8 w-14 h-14 rounded-full bg-primary-200/25"></div>

        <div className="relative z-10">
          <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-accent-500 flex items-center justify-center">
            <i className="ri-gallery-line text-3xl text-background-50"></i>
          </div>
          <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-1">
            우리 가족 앨범
          </h1>
          <p className="text-sm text-foreground-500">
            소중한 추억을 함께 나누는 공간
          </p>
        </div>
      </div>

      {/* 로그인 폼 */}
      <div className="flex-1 px-6 pb-10">
        <div className="bg-background-50 rounded-2xl p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* 에러 메시지 */}
            {error && (
              <div className="bg-accent-100/50 border border-accent-300/50 rounded-xl px-4 py-3 flex items-start gap-2.5">
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-error-warning-line text-sm text-accent-600"></i>
                </div>
                <p className="text-sm text-accent-700 leading-relaxed">{error}</p>
              </div>
            )}

            {/* 이메일 */}
            <div>
              <label htmlFor="login-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                이메일
              </label>
              <div className="relative">
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                  <i className="ri-mail-line text-base text-foreground-400"></i>
                </div>
                <input
                  id="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="family@example.com"
                  autoComplete="email"
                  className="w-full h-12 pl-10 pr-4 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all"
                />
              </div>
            </div>

            {/* 비밀번호 */}
            <div>
              <label htmlFor="login-password" className="block text-sm font-medium text-foreground-700 mb-1.5">
                비밀번호
              </label>
              <div className="relative">
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                  <i className="ri-lock-line text-base text-foreground-400"></i>
                </div>
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="비밀번호 입력"
                  autoComplete="current-password"
                  className="w-full h-12 pl-10 pr-12 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200 transition-colors cursor-pointer"
                  aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
                >
                  <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-base text-foreground-400`}></i>
                </button>
              </div>
            </div>

            {/* 로그인 상태 유지 + 비밀번호 찾기 */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-background-300 text-accent-500 focus:ring-accent-400 focus:ring-offset-0 cursor-pointer"
                />
                <span className="text-xs text-foreground-500 group-hover:text-foreground-700 transition-colors whitespace-nowrap">
                  로그인 상태 유지
                </span>
              </label>
              <button
                type="button"
                onClick={() => setShowResetInfo(!showResetInfo)}
                className="text-xs text-foreground-500 hover:text-accent-500 transition-colors cursor-pointer whitespace-nowrap"
              >
                비밀번호 찾기
              </button>
            </div>

            {/* 비밀번호 찾기 안내 */}
            {showResetInfo && (
              <div className="bg-secondary-50 rounded-xl px-4 py-3 border border-secondary-200/50">
                <div className="flex items-start gap-2.5">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-information-line text-sm text-secondary-600"></i>
                  </div>
                  <p className="text-xs text-foreground-600 leading-relaxed">
                    비밀번호를 잊으셨나요? 가족 대표에게 문의하여 비밀번호 재설정을 요청해주세요. 대표 이메일로 재설정 링크가 발송됩니다.
                  </p>
                </div>
              </div>
            )}

            {/* 로그인 버튼 */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full h-12 bg-accent-500 hover:bg-accent-600 disabled:bg-accent-300 text-background-50 rounded-xl text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-background-50/30 border-t-background-50 rounded-full animate-spin"></div>
                  <span>로그인 중...</span>
                </>
              ) : (
                '로그인'
              )}
            </button>
          </form>

          {/* 구분선 */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-background-200/70"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="px-3 bg-background-50 text-xs text-foreground-400">또는</span>
            </div>
          </div>

          {/* 회원가입 버튼 */}
          <Link
            to="/signup"
            className="w-full h-12 border-2 border-accent-200 hover:border-accent-400 text-accent-600 rounded-xl text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-user-add-line"></i>
            가족 회원가입
          </Link>
          <Link to="/install" className="mt-4 block text-center text-sm font-semibold text-foreground-600 underline underline-offset-4">휴대폰에 앱 설치하기</Link>
        </div>
      </div>
    </div>
  );
}